import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { makeRequest } from "../../../utils/axios.js"; 
import { AuthContext } from "../../../context/authContext";

export const useProfileLogic = () => {
    const { currentUser } = useContext(AuthContext);
    const userId = parseInt(useLocation().pathname.split("/")[2]);
    const queryClient = useQueryClient();

    const { isLoading: uIsLoading, data: userData } = useQuery({
        queryKey: ["user", userId],
        queryFn: () => makeRequest.get("/users/find/" + userId).then((res) => res.data),
    });

    const { isLoading: rIsLoading, data: relationshipData } = useQuery({
        queryKey: ["relationship", userId],
        queryFn: () => makeRequest.get("/relationships?followedUserId=" + userId).then((res) => res.data),
    });

    const { isLoading: fIsLoading, data: friendStatus } = useQuery({
        queryKey: ["friendship", userId],
        queryFn: () => makeRequest.get("/friendships?userId=" + userId).then((res) => res.data),
    });

    const followMutation = useMutation({
        mutationFn: (following) => {
            if (following) return makeRequest.delete("/relationships?userId=" + userId);
            return makeRequest.post("/relationships", { userId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["relationship", userId] });
        },
    });

    const friendMutation = useMutation({
        mutationFn: (status) => {
            if (status === "friends" || status === "sent") {
                return makeRequest.delete("/friendships?userId=" + userId);
            }
            if (status === "received") {
                return makeRequest.put("/friendships/confirm", { userId });
            }
            return makeRequest.post("/friendships", { userId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["friendship", userId] });
            queryClient.invalidateQueries({ queryKey: ["friendRequests"] });
            queryClient.invalidateQueries({ queryKey: ["relationship", userId] });
            queryClient.invalidateQueries({ queryKey: ["suggestions"] });
        },
    });

    const handleFollow = () => {
        followMutation.mutate(relationshipData.includes(currentUser.id));
    };

    const handleFriendAction = () => {
        friendMutation.mutate(friendStatus);
    };

    const isOwnProfile = userId === currentUser.id;

    return {
        loading: uIsLoading,
        userData,
        relationship: {
            isLoading: rIsLoading,
            data: relationshipData,
            isFollowing: relationshipData?.includes(currentUser.id),
        },
        friendship: {
            isLoading: fIsLoading,
            status: friendStatus,
        },
        actions: {
            handleFollow,
            handleFriendAction,
        },
        isOwnProfile,
        userId
    };
};